import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AssistantProvider } from "@/context/AssistantContext";
import ScrollProvider from "@/components/ScrollProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import AskAyurvedam from "@/components/AskAyurvedam";
import Home from "@/pages/Home";
import HomeLanding from "@/pages/HomeLanding";
import Articles from "@/pages/Articles";
import ArticleDetail from "@/pages/ArticleDetail";
import Herbs from "@/pages/Herbs";
import HerbDetail from "@/pages/HerbDetail";
import Therapies from "@/pages/Therapies";
import TherapyDetail from "@/pages/TherapyDetail";
import AuthorDetail from "@/pages/AuthorDetail";
import TagArchive from "@/pages/TagArchive";
import SearchPage from "@/pages/SearchPage";
import Network from "@/pages/Network";
import Assistants from "@/pages/Assistants";
import Login from "@/pages/Login";
import Dashboard from "@/pages/admin/Dashboard";
import { Link } from "react-router-dom";

function NotFound() {
  return (
    <div className="mx-auto max-w-xl px-5 py-28 text-center">
      <p className="font-heading text-6xl text-brand">404</p>
      <h1 className="mt-4 font-heading text-3xl text-brand-ink">Page not found</h1>
      <p className="mt-3 text-brand-muted">
        This page may have moved during our migration. Explore our reviewed Ayurveda content instead.
      </p>
      <Link
        to="/"
        data-testid="notfound-home-link"
        className="mt-6 inline-flex rounded-full bg-brand px-6 py-3 font-medium text-brand-sand"
      >
        Back to home
      </Link>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="grid min-h-screen place-items-center text-brand-muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicLayout({ children }) {
  return (
    <div className="App flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1 pb-16">{children}</main>
      <AskAyurvedam />
      <Footer />
      <ChatWidget />
    </div>
  );
}

function Shell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin") || location.pathname === "/login";

  if (isAdmin) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/admin" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      </Routes>
    );
  }

  return (
    <PublicLayout>
      <Routes>
        <Route path="/" element={<HomeLanding />} />
        <Route path="/about" element={<Home />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/articles/:slug" element={<ArticleDetail />} />
        <Route path="/herbs" element={<Herbs />} />
        <Route path="/herbs/:slug" element={<HerbDetail />} />
        <Route path="/therapies" element={<Therapies />} />
        <Route path="/therapies/:slug" element={<TherapyDetail />} />
        <Route path="/authors/:slug" element={<AuthorDetail />} />
        <Route path="/tags/:slug" element={<TagArchive />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/network" element={<Network />} />
        <Route path="/assistants" element={<Assistants />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </PublicLayout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AssistantProvider>
          <ScrollProvider>
            <Shell />
          </ScrollProvider>
        </AssistantProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
